import { agencyName, initials } from './format.ts';
import type { PlatformNotice } from './notice.ts';

/** Who is signed in and what their roles let them see (the API decides; this only shapes the UI). */

export type Me = {
  memberId: string;
  email: string;
  /**
   * How the person is called, from their session (#260). Null or absent in a session issued
   * before it carried the name: the console then names them by their e-mail, as before.
   */
  name?: string | null;
  /** AGENCY for an agency's people; NUBI for Nubi's own organization (ADR-0023). */
  organizationType: 'AGENCY' | 'CARRIER' | 'NUBI';
  /** The person's agency; null outside an agency (Nubi's organization). */
  agencyId: string | null;
  agentId: string | null;
  roles: string[];
  /** The agency's name as the registry of agencies has it (ADR-0024); agency people only. */
  agencyName?: string;
  /** The platform's notice in force (ADR-0028); agency people only, null when there is none. */
  notice?: PlatformNotice | null;
  /**
   * The peripheral modules the administration of Nubi turned off for the agency (ADR-0029): they
   * leave its menu. Agency people only; absent or empty, every module is on.
   */
  modulesOff?: string[];
};

export let me: Me | null = null;
export function setMe(m: Me | null) {
  me = m;
}

/** The person's name, or null while their session carries none (#260). */
export const nameOf = (m: Pick<Me, 'name'> | null = me) => m?.name?.trim() || null;

/**
 * The sidebar's user block (#260, B3): the person's name, their e-mail as a hint and the monogram
 * of their name; without a name, their e-mail and two of its letters, as before.
 */
export function userBlock(m: Pick<Me, 'name' | 'email'>) {
  const name = nameOf(m);
  const fromEmail = m.email.replace(/[^a-z]/gi, '').slice(0, 2);
  return name
    ? { who: name, hint: m.email, monogram: initials(name) || fromEmail }
    : { who: m.email, hint: null, monogram: fromEmail };
}

export const has = (...roles: string[]) => !!me && me.roles.some((r) => roles.includes(r));
/**
 * A person of Nubi's own organization (ADR-0023): the console shows them «Administración de
 * Nubi» instead of an agency's menu. Agency people never are.
 */
export const isNubiOrg = () => me?.organizationType === 'NUBI';
/** La administración de Nubi: reads the platform's agencies (the API and Cerbos decide). */
export const administersNubi = () => isNubiOrg() && has('nubi_admin');
/** Who the person works for, as the header and each page's eyebrow say it. */
export const workplace = () =>
  !me
    ? 'Nubi'
    : isNubiOrg()
      ? 'Administración de Nubi'
      : (me.agencyName ?? agencyName(me.agencyId));
export const canQuote = () => has('agency_director', 'agency_admin', 'agent');
/** Asks the carriers' knowledge (Conocimiento): the ones who sell and their management. */
export const canAskKnowledge = () => has('agency_director', 'agency_admin', 'agent');
export const runsCashBox = () => has('agency_admin', 'cashier');
export const supervises = () => has('agency_director', 'agency_admin');
/** Runs agent onboarding (creates candidates, completes steps); the agency manager follows it. */
export const runsOnboarding = () => has('agency_director', 'agency_admin');
/** Follows the onboardings: management, and the agency manager who approves codes (ADR-0013). */
export const followsOnboarding = () => runsOnboarding() || has('agency_manager');
/** Builds courses and their exams: management and the training manager (ADR-0013). */
export const buildsCourses = () => supervises() || has('training_manager');
/** The background-check queue: the evaluator decides; management and the agency manager watch it. */
export const seesBackgroundChecks = () =>
  has('evaluator', 'agency_director', 'agency_admin', 'agency_manager');
/**
 * Follows the renewals («Renovaciones», ADR-0034): management, the agency manager and the agents
 * (their own). The cashier and the collections officer do not.
 */
export const followsRenewals = () =>
  has('agency_director', 'agency_admin', 'agency_manager', 'agent');
/**
 * Follows the claims («Siniestros», ADR-0038): management, the agency manager (reads) and the
 * agents (their own policies'). The cashier and the collections officer do not.
 */
export const followsClaims = () =>
  has('agency_director', 'agency_admin', 'agency_manager', 'agent');
/** Sees the portfolio (policies, receipts, statistics): every role but evaluation and training. */
export const seesPortfolio = () =>
  has(
    'agency_director',
    'agency_admin',
    'agency_manager',
    'cashier',
    'collections_officer',
    'agent',
  );
